import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"
import { useEffect, useState } from "react"
import { Button } from "../../components/ui/button"
import { Checkbox } from "../../components/ui/checkbox"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { formatCpfCnpj, sanitizeDocumentInput } from "../../lib/brazilianDocument"

type ClientFormData = {
  id?: number
  cliente: string
  codigo_sap?: string | null
  cr?: string | null
  cpf_cnpj_tomador: string
  inscricao_municipal_tomador: string
  inscricao_estadual_tomador: string
  razao_social_tomador: string
  tipo_endereco_tomador: string
  endereco_tomador: string
  numero_endereco_tomador: string
  complemento_endereco_tomador: string
  bairro_tomador: string
  cidade_tomador: string
  uf_tomador: string
  cep_tomador: string
  exterior: boolean
  omie_n_cod_cli: string
}

type ClientFormProps = {
  initialValues: ClientFormData
  submitUrl: string
  submitLabel: string
  method: "post" | "patch"
  errors?: Record<string, string[]>
}

function onlyDigits(value: string) {
  return value.replace(/\D/g, "")
}

function formatCep(value: string) {
  const digits = onlyDigits(value).slice(0, 8)
  return digits.replace(/^(\d{5})(\d)/, "$1-$2")
}

function onlyLetters(value: string) {
  return value.replace(/[^a-zA-Z]/g, "").toUpperCase()
}

type ViaCepResponse = {
  cep?: string
  logradouro?: string
  complemento?: string
  bairro?: string
  localidade?: string
  uf?: string
  erro?: boolean
}

function ErrorText({ value }: { value?: string[] }) {
  if (!value?.length) return null
  return <p className="text-xs text-rose-400">{value[0]}</p>
}

export function ClientForm({ initialValues, submitUrl, submitLabel, method, errors }: ClientFormProps) {
  const [isCepLoading, setIsCepLoading] = useState(false)
  const [cepMessage, setCepMessage] = useState("")
  const [lastCepLookup, setLastCepLookup] = useState("")

  const form = useForm({
    client: {
      cliente: initialValues.cliente ?? "",
      codigo_sap: initialValues.codigo_sap ?? "",
      cr: initialValues.cr ?? "",
      cpf_cnpj_tomador: sanitizeDocumentInput(initialValues.cpf_cnpj_tomador ?? ""),
      inscricao_municipal_tomador: initialValues.inscricao_municipal_tomador ?? "",
      inscricao_estadual_tomador: initialValues.inscricao_estadual_tomador ?? "",
      razao_social_tomador: initialValues.razao_social_tomador ?? "",
      tipo_endereco_tomador: initialValues.tipo_endereco_tomador ?? "",
      endereco_tomador: initialValues.endereco_tomador ?? "",
      numero_endereco_tomador: initialValues.numero_endereco_tomador ?? "",
      complemento_endereco_tomador: initialValues.complemento_endereco_tomador ?? "",
      bairro_tomador: initialValues.bairro_tomador ?? "",
      cidade_tomador: initialValues.cidade_tomador ?? "",
      uf_tomador: onlyLetters(initialValues.uf_tomador ?? "").slice(0, 2),
      cep_tomador: onlyDigits(initialValues.cep_tomador ?? ""),
      exterior: Boolean(initialValues.exterior),
      omie_n_cod_cli: initialValues.omie_n_cod_cli?.toString() ?? ""
    }
  })

  useEffect(() => {
    const cep = form.data.client.cep_tomador
    if (cep.length !== 8) {
      if (cepMessage) setCepMessage("")
      return
    }
    if (cep === lastCepLookup) return

    let cancelled = false

    async function fetchCep() {
      setIsCepLoading(true)
      setCepMessage("")

      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
        const data = (await response.json()) as ViaCepResponse

        if (!response.ok || data.erro) {
          if (!cancelled) setCepMessage("CEP não encontrado.")
          return
        }

        if (!cancelled) {
          setLastCepLookup(cep)
          setCepMessage("Endereço preenchido automaticamente.")
          form.setData("client", {
            ...form.data.client,
            endereco_tomador: data.logradouro ?? "",
            bairro_tomador: data.bairro ?? "",
            cidade_tomador: data.localidade ?? "",
            uf_tomador: onlyLetters(data.uf ?? "").slice(0, 2),
            complemento_endereco_tomador:
              form.data.client.complemento_endereco_tomador || (data.complemento ?? "")
          })
        }
      } catch {
        if (!cancelled) setCepMessage("Não foi possível consultar o CEP agora.")
      } finally {
        if (!cancelled) setIsCepLoading(false)
      }
    }

    void fetchCep()

    return () => {
      cancelled = true
    }
  }, [form, form.data.client, cepMessage, lastCepLookup])

  function updateField<K extends keyof ClientFormData>(field: K, value: ClientFormData[K]) {
    form.setData("client", {
      ...form.data.client,
      [field]: value
    })
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (method === "patch") {
      form.patch(submitUrl)
      return
    }
    form.post(submitUrl)
  }

  return (
    <form onSubmit={submit} className="space-y-6 rounded-xl border border-slate-800 bg-slate-900 p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="cliente">Cliente</Label>
          <Input id="cliente" value={form.data.client.cliente} onChange={(e) => updateField("cliente", e.target.value)} />
          <ErrorText value={errors?.cliente} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="codigo_sap">Código SAP</Label>
          <Input
            id="codigo_sap"
            inputMode="numeric"
            value={form.data.client.codigo_sap}
            onChange={(e) => updateField("codigo_sap", e.target.value.trim())}
          />
          <ErrorText value={errors?.codigo_sap} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cr">CR</Label>
          <Input id="cr" value={form.data.client.cr} onChange={(e) => updateField("cr", e.target.value.trim())} />
          <ErrorText value={errors?.cr} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="omie_n_cod_cli">Código do cliente Omie (nCodCli)</Label>
          <Input
            id="omie_n_cod_cli"
            inputMode="numeric"
            placeholder="Ex.: 2485994"
            value={form.data.client.omie_n_cod_cli}
            onChange={(e) => updateField("omie_n_cod_cli", onlyDigits(e.target.value))}
            className="border-slate-700 bg-slate-950 text-white"
          />
          <ErrorText value={errors?.omie_n_cod_cli} />
          <p className="text-xs text-slate-500">ID do cadastro do cliente na Omie (ListarClientes / ConsultarCliente).</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="cpf_cnpj_tomador">CPF/CNPJ do Tomador</Label>
          <Input
            id="cpf_cnpj_tomador"
            placeholder="000.000.000-00 ou 00.000.000/0000-00"
            value={formatCpfCnpj(form.data.client.cpf_cnpj_tomador)}
            onChange={(e) => updateField("cpf_cnpj_tomador", sanitizeDocumentInput(e.target.value))}
          />
          <ErrorText value={errors?.cpf_cnpj_tomador} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cep_tomador">CEP</Label>
          <Input
            id="cep_tomador"
            inputMode="numeric"
            placeholder="00000-000"
            value={formatCep(form.data.client.cep_tomador)}
            onChange={(e) => {
              if (cepMessage) setCepMessage("")
              updateField("cep_tomador", onlyDigits(e.target.value).slice(0, 8))
            }}
          />
          <ErrorText value={errors?.cep_tomador} />
          {!errors?.cep_tomador && (
            <p className="text-xs text-slate-400">
              {isCepLoading ? "Consultando CEP..." : cepMessage}
            </p>
          )}
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="razao_social_tomador">Razão Social do Tomador</Label>
          <Input id="razao_social_tomador" value={form.data.client.razao_social_tomador} onChange={(e) => updateField("razao_social_tomador", e.target.value)} />
          <ErrorText value={errors?.razao_social_tomador} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="endereco_tomador">Endereço do Tomador</Label>
          <Input id="endereco_tomador" value={form.data.client.endereco_tomador} onChange={(e) => updateField("endereco_tomador", e.target.value)} />
          <ErrorText value={errors?.endereco_tomador} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="numero_endereco_tomador">Número</Label>
          <Input id="numero_endereco_tomador" value={form.data.client.numero_endereco_tomador} onChange={(e) => updateField("numero_endereco_tomador", e.target.value)} />
          <ErrorText value={errors?.numero_endereco_tomador} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="complemento_endereco_tomador">Complemento</Label>
          <Input id="complemento_endereco_tomador" value={form.data.client.complemento_endereco_tomador} onChange={(e) => updateField("complemento_endereco_tomador", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bairro_tomador">Bairro</Label>
          <Input id="bairro_tomador" value={form.data.client.bairro_tomador} onChange={(e) => updateField("bairro_tomador", e.target.value)} />
          <ErrorText value={errors?.bairro_tomador} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cidade_tomador">Cidade</Label>
          <Input id="cidade_tomador" value={form.data.client.cidade_tomador} onChange={(e) => updateField("cidade_tomador", e.target.value)} />
          <ErrorText value={errors?.cidade_tomador} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="uf_tomador">UF</Label>
          <Input
            id="uf_tomador"
            maxLength={2}
            value={form.data.client.uf_tomador}
            onChange={(e) => updateField("uf_tomador", onlyLetters(e.target.value).slice(0, 2))}
          />
          <ErrorText value={errors?.uf_tomador} />
        </div>
        <div className="flex items-center gap-3 md:col-span-2">
          <Checkbox
            id="exterior"
            checked={form.data.client.exterior}
            onChange={(e) => updateField("exterior", e.target.checked)}
          />
          <Label htmlFor="exterior" className="cursor-pointer font-normal text-slate-200">
            Tomador no exterior (equivalente ao campo <span className="font-mono text-slate-400">exterior</span> na Omie)
          </Label>
        </div>
        <div className="space-y-2">
          <Label htmlFor="tipo_endereco_tomador">Tipo de Endereço</Label>
          <Input id="tipo_endereco_tomador" value={form.data.client.tipo_endereco_tomador} onChange={(e) => updateField("tipo_endereco_tomador", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="inscricao_municipal_tomador">Inscrição Municipal</Label>
          <Input id="inscricao_municipal_tomador" value={form.data.client.inscricao_municipal_tomador} onChange={(e) => updateField("inscricao_municipal_tomador", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="inscricao_estadual_tomador">Inscrição Estadual</Label>
          <Input id="inscricao_estadual_tomador" value={form.data.client.inscricao_estadual_tomador} onChange={(e) => updateField("inscricao_estadual_tomador", e.target.value)} />
        </div>
      </div>

      <p className="text-sm text-slate-500">
        Código de serviço da NF, alíquotas e retenções ficam em{" "}
        <Link href={initialValues.id ? `/clientes/${initialValues.id}/configuracoes-fiscais` : "/clientes"} className="text-brand-500 underline">
          Config. fiscal
        </Link>
        {initialValues.id ? "" : " (disponível após salvar o cliente)."}
      </p>

      <div className="flex items-center justify-end gap-3">
        <Button asChild type="button" variant="outline" className="border-slate-700 bg-transparent">
          <Link href="/clientes">Cancelar</Link>
        </Button>
        <Button type="submit" disabled={form.processing}>
          {form.processing ? "Salvando..." : submitLabel}
        </Button>
      </div>
    </form>
  )
}
